import { Geolocation } from '@capacitor/geolocation';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { api } from './api';

// Plugin nativo que expone verificación real de permisos especiales Android
interface DevicePluginInterface {
    isBatteryOptimizationIgnored(): Promise<{ isIgnored: boolean }>;
    requestBatteryOptimization(): Promise<void>;
    canDrawOverlays(): Promise<{ canDraw: boolean }>;
    requestOverlayPermission(): Promise<void>;
    getPermissionStatus(): Promise<FullPermissionStatus>;
    openAppSettings(): Promise<{ opened: boolean }>;
    openNotificationSettings(): Promise<{ opened: boolean }>;
    openFullScreenIntentSettings(): Promise<{ opened: boolean }>;
    openAutostartSettings(): Promise<{ opened: boolean; fallback: boolean }>;
}
const DevicePlugin = registerPlugin<DevicePluginInterface>('Device');

/** Foto real del estado de cada permiso + datos del teléfono (ver DevicePlugin.java). */
export interface FullPermissionStatus {
    notifications: boolean;
    location: boolean;
    locationPrecise: boolean;
    battery: boolean;
    overlay: boolean;
    fullScreen: boolean;
    doseChannelBlocked: boolean;
    manufacturer: string;
    model: string;
    sdk: number;
    release: string;
    oemKnown: boolean;
}

export interface PermissionStatus {
    location: 'granted' | 'denied' | 'prompt';
    notifications: 'granted' | 'denied' | 'prompt';
}

export class PermissionsService {
    private static logCallback: ((message: string, type?: 'info' | 'success' | 'warning' | 'error') => void) | null = null;

    static setLogger(callback: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void) {
        this.logCallback = callback;
    }

    private static log(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') {
        console.log(message);
        if (this.logCallback) {
            this.logCallback(message, type);
        }
    }

    /**
     * Verifica el estado actual de todos los permisos
     */
    static async checkPermissions(): Promise<PermissionStatus> {
        if (!Capacitor.isNativePlatform()) {
            this.log('⚠️ [PERMISOS] No es plataforma nativa - Omitiendo verificación', 'warning');
            return { location: 'granted', notifications: 'granted' };
        }

        this.log('🔍 [PERMISOS] Verificando estado de permisos...', 'info');

        const status: PermissionStatus = {
            location: 'prompt',
            notifications: 'prompt'
        };

        try {
            // Verificar permisos de ubicación
            const locationPerm = await Geolocation.checkPermissions();
            status.location = locationPerm.location as any;
            this.log(`📍 [PERMISOS] Ubicación: ${status.location}`, status.location === 'granted' ? 'success' : 'warning');

            // Verificar permisos de notificaciones
            const notifPerm = await FirebaseMessaging.checkPermissions();
            status.notifications = notifPerm.receive as any;
            this.log(`🔔 [PERMISOS] Notificaciones: ${status.notifications}`, status.notifications === 'granted' ? 'success' : 'warning');

        } catch (error: any) {
            this.log(`❌ [PERMISOS] Error al verificar: ${error.message}`, 'error');
        }

        return status;
    }

    /**
     * Solicita permisos de ubicación
     */
    static async requestLocationPermission(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) {
            return true;
        }

        try {
            this.log('📍 [PERMISOS] Solicitando permiso de ubicación...', 'info');
            const result = await Geolocation.requestPermissions();
            const granted = result.location === 'granted';

            if (granted) {
                this.log('✅ [PERMISOS] Ubicación CONCEDIDA', 'success');
            } else {
                this.log('❌ [PERMISOS] Ubicación DENEGADA', 'error');
            }

            return granted;
        } catch (error: any) {
            this.log(`❌ [PERMISOS] Error solicitando ubicación: ${error.message}`, 'error');
            return false;
        }
    }

    /**
     * Solicita permisos de notificaciones
     */
    static async requestNotificationPermission(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) {
            return true;
        }

        try {
            this.log('🔔 [PERMISOS] Solicitando permiso de notificaciones...', 'info');
            const result = await FirebaseMessaging.requestPermissions();
            const granted = result.receive === 'granted';

            if (granted) {
                this.log('✅ [PERMISOS] Notificaciones CONCEDIDAS', 'success');
            } else {
                this.log('❌ [PERMISOS] Notificaciones DENEGADAS', 'error');
            }

            return granted;
        } catch (error: any) {
            this.log(`❌ [PERMISOS] Error solicitando notificaciones: ${error.message}`, 'error');
            return false;
        }
    }

    /**
     * Obtiene la ubicación actual del usuario
     */
    static async getCurrentLocation(): Promise<{ lat: number; lng: number } | null> {
        if (!Capacitor.isNativePlatform()) {
            this.log('⚠️ [GPS] No es plataforma nativa - Omitiendo GPS', 'warning');
            return null;
        }

        try {
            this.log('📍 [GPS] Obteniendo ubicación actual...', 'info');
            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000
            });

            const location = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };

            this.log(`✅ [GPS] Ubicación obtenida: ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`, 'success');
            return location;
        } catch (error: any) {
            this.log(`❌ [GPS] Error obteniendo ubicación: ${error.message}`, 'error');
            return null;
        }
    }

    /**
     * Guarda la ubicación del usuario en el backend
     */
    static async saveUserLocation(userId: string, lat: number, lng: number): Promise<boolean> {
        try {
            this.log(`💾 [GPS] Guardando ubicación en backend...`, 'info');
            const response = await api.updateUserLocation(userId, lat, lng);

            if (response.success) {
                this.log(`✅ [GPS] Ubicación guardada correctamente`, 'success');
                return true;
            } else {
                this.log(`❌ [GPS] Error al guardar: ${response.error || 'Unknown'}`, 'error');
                return false;
            }
        } catch (error: any) {
            this.log(`❌ [GPS] Error guardando ubicación: ${error.message}`, 'error');
            return false;
        }
    }

    /**
     * Flujo completo: Solicita todos los permisos necesarios
     */
    static async requestAllPermissions(): Promise<PermissionStatus> {
        this.log('🚀 [PERMISOS] Iniciando solicitud de todos los permisos...', 'info');

        const locationGranted = await this.requestLocationPermission();
        const notificationsGranted = await this.requestNotificationPermission();

        const status: PermissionStatus = {
            location: locationGranted ? 'granted' : 'denied',
            notifications: notificationsGranted ? 'granted' : 'denied'
        };

        this.log(`📊 [PERMISOS] Estado final: GPS=${status.location}, Notif=${status.notifications}`,
            (status.location === 'granted' && status.notifications === 'granted') ? 'success' : 'warning');

        return status;
    }

    /**
     * Actualiza la ubicación del usuario (GPS) en el backend
     */
    static async updateUserLocationInBackground(userId: string): Promise<void> {
        const location = await this.getCurrentLocation();
        if (location) {
            await this.saveUserLocation(userId, location.lat, location.lng);
        }
    }

    // ── Permisos especiales Android (via DevicePlugin nativo) ───────────

    /** Verifica si la app está exenta de optimización de batería. */
    static async checkBatteryOptimization(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) return true;
        try {
            const { isIgnored } = await DevicePlugin.isBatteryOptimizationIgnored();
            return isIgnored;
        } catch {
            return false;
        }
    }

    /** Abre el diálogo del sistema para eximir la optimización de batería de esta app. */
    static async requestBatteryOptimizationPermission(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await DevicePlugin.requestBatteryOptimization();
        } catch {}
    }

    /** Verifica si la app tiene permiso de dibujar sobre otras apps (overlay). */
    static async checkOverlayPermission(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) return true;
        try {
            const { canDraw } = await DevicePlugin.canDrawOverlays();
            return canDraw;
        } catch {
            return false;
        }
    }

    /** Abre los ajustes de overlay para esta app. */
    static async requestOverlayPermissionIntent(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await DevicePlugin.requestOverlayPermission();
        } catch {}
    }

    // ── Pantalla "Estado de permisos" ───────────────────────────────────

    /** Estado real de todos los permisos + datos del teléfono. null si no es la app nativa o falla. */
    static async getFullStatus(): Promise<FullPermissionStatus | null> {
        if (!Capacitor.isNativePlatform()) return null;
        try {
            return await DevicePlugin.getPermissionStatus();
        } catch (error: any) {
            this.log(`❌ [PERMISOS] No se pudo leer el estado completo: ${error?.message}`, 'error');
            return null;
        }
    }

    static async openAppSettings(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try { await DevicePlugin.openAppSettings(); } catch {}
    }

    static async openNotificationSettings(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try { await DevicePlugin.openNotificationSettings(); } catch {}
    }

    static async openFullScreenIntentSettings(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try { await DevicePlugin.openFullScreenIntentSettings(); } catch {}
    }

    /** Abre la pantalla de autoinicio del fabricante (o los ajustes de la app si no hay una conocida). */
    static async openAutostartSettings(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try { await DevicePlugin.openAutostartSettings(); } catch {}
    }

    /**
     * Corrige un permiso de "diálogo normal" (ubicación / notificaciones): si
     * Android todavía lo puede preguntar, lo pregunta; si ya quedó denegado de
     * forma permanente (el diálogo ya no aparece), abre directo los ajustes.
     */
    static async fixLocation(): Promise<void> {
        const perm = await Geolocation.checkPermissions().catch(() => null);
        if (perm && perm.location === 'prompt') {
            await this.requestLocationPermission();
        } else {
            await this.openAppSettings();
        }
    }

    static async fixNotifications(): Promise<void> {
        const perm = await FirebaseMessaging.checkPermissions().catch(() => null);
        if (perm && perm.receive === 'prompt') {
            await this.requestNotificationPermission();
        } else {
            await this.openNotificationSettings();
        }
    }
}
